import { useMemo, useEffect, useState } from 'react'
import { useEndDateForFinancialInsights } from '@/utils/sharedHooks'
import { DateTime } from 'luxon'
import { DropdownType, InsightsDividedCard } from './InsightsDividedCard'
import { MONTHS_ARR, INSIGHTS_TYPE } from './utils'
import { useAppDispatch } from '@/utils/typeHelpers'
import { fetchInsightsTableDropdownOptions } from './financeActions'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { Select } from '@/ui'

export const useInsightsDatePicker = (
  monthParam?: string | null,
  yearParam?: string | null
) => {
  const [yearFilter, setYearFilter] = useState<string>()
  const [monthFilter, setMonthFilter] = useState<string>()

  useEffect(() => {
    if (yearParam) {
      setYearFilter(yearParam)
    }
    if (monthParam) {
      setMonthFilter(monthParam)
    }
  }, [yearParam, monthParam])

  const inputDate = useMemo(() => {
    if (yearFilter && monthFilter) {
      const month = parseInt(monthFilter) + 1
      const datetimeObj = DateTime.fromObject({
        year: parseInt(yearFilter),
        month,
      })
        .endOf('month')
        .plus({ days: 15 })
      return datetimeObj.toFormat(DATE_FORMATS_LUXON.INPUT)
    }
    return undefined
  }, [yearFilter, monthFilter])

  return {
    yearFilter,
    monthFilter,
    inputDate,
    setYearFilter,
    setMonthFilter,
  }
}

export const InsightsDatePicker = ({
  monthFilter,
  yearFilter,
  handleMonthFilterChange,
  handleYearFilterChange,
  insightsType = INSIGHTS_TYPE.PROFIT,
}: {
  monthFilter?: string
  yearFilter?: string
  handleMonthFilterChange: (month: string) => void
  handleYearFilterChange: (year: string) => void
  insightsType?: INSIGHTS_TYPE
}) => {
  const [years, setYears] = useState<number[]>([])
  const dispatch = useAppDispatch()
  const currentDate = useMemo(() => DateTime.now(), [])
  const endDate = useEndDateForFinancialInsights(currentDate)

  useEffect(() => {
    if (years?.length && !yearFilter) {
      handleYearFilterChange(String(years[0]))
    } else if (years && !years?.length && !yearFilter) {
      handleYearFilterChange(String(DateTime.now().year))
    }
  }, [years, yearFilter, handleYearFilterChange])

  const { filteredYearOptions, filteredMonthOptions } = useMemo(() => {
    const yearOptions = [...years]
    const filteredYears = [
      endDate.year,
      ...yearOptions.filter((year) => year < endDate.year),
    ]

    const monthsWithoutYTD = MONTHS_ARR.filter((month) => month.value !== 'ytd')
    let filteredMonths = [...monthsWithoutYTD]
    if (yearFilter && parseInt(yearFilter) === endDate.year) {
      filteredMonths = filteredMonths.filter(
        (monthOption) => parseInt(monthOption.value) < endDate.month
      )

      const foundMonth = filteredMonths.findIndex(
        (month) => month.value === monthFilter
      )

      if (!monthFilter || foundMonth === -1) {
        handleMonthFilterChange(filteredMonths[filteredMonths.length - 1].value)
      }
    }

    return {
      filteredYearOptions: filteredYears.map((y) => ({
        value: String(y),
        text: String(y),
      })),
      filteredMonthOptions: filteredMonths,
    }
  }, [yearFilter, endDate, monthFilter, handleMonthFilterChange, years])

  useEffect(() => {
    const fetchYears = async () => {
      const yearsData =
        await fetchInsightsTableDropdownOptions(insightsType)(dispatch)
      setYears(yearsData?.length ? yearsData : [])
    }
    fetchYears()
  }, [dispatch, insightsType])

  return (
    <InsightsDividedCard
      columns={[
        {
          content: (
            <div className="w-full min-w-[100px]">
              <Select
                value={yearFilter}
                options={filteredYearOptions}
                onValueChange={(value) => handleYearFilterChange(value ?? '')}
              />
            </div>
          ),
        },
        {
          content: (
            <div className="w-full flex-1 sm:flex-none sm:min-w-[150px]">
              <Select
                value={monthFilter}
                options={filteredMonthOptions}
                onValueChange={(value) => handleMonthFilterChange(value ?? '')}
              />
            </div>
          ),
        },
      ]}
      type={DropdownType.dropdown}
    />
  )
}
